import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import Unit from 'Components/Unit';
import { styled } from '@mui/system';
import { StyledTitle } from '..';
import SeizureClassificationComponent from './components/SeizureClassificationComponent';
import type { TEpilepsyESSeizureFrequencyType } from '../../definitions/epilepsy';
import { fm } from 'Components/FormatMessage';

const InlineDiv = styled('div')({
  display: 'inline-flex',
});

const SeizureFrequencyField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const thisSearchField = searchFields.seizureFrequency !== 'noCropping' ? searchFields.seizureFrequency : undefined;

  const criteria: Array<TSeizureClassification | undefined> = thisSearchField?.classification ?? [];

  const onChangeCriteria = (_i: number) => (values: TOnChangeValues) => {
    const arr = values['seizureClassification'] as
      | Array<{ classification: TSeizureClassification; group: string }>
      | undefined;
    const classifications = arr?.map(a => a.classification) ?? [];
    const base = typeof thisSearchField === 'object' ? thisSearchField : {};
    const newValue = { ...base, classification: classifications.length > 0 ? classifications : undefined };
    onChange({
      seizureFrequency:
        newValue.classification === undefined && newValue.seizures === undefined && newValue.periods === undefined
          ? 'noCropping'
          : newValue,
    });
  };

  const handleSeizuresChange = (values: TOnChangeValues) => {
    const f = Object.keys(values)[0] as 'minSeizures' | 'maxSeizures';
    const v = values[f] as number | undefined;
    const base: TEpilepsyESSeizureFrequencyType = typeof thisSearchField === 'object' ? thisSearchField : {};
    const currentSeizures = base.seizures ?? { minSeizures: undefined, maxSeizures: undefined };
    const newSeizures = { ...currentSeizures, [f]: v };
    const noSeizures = newSeizures.minSeizures === undefined && newSeizures.maxSeizures === undefined;
    const newValue = { ...base, seizures: noSeizures ? undefined : newSeizures };
    onChange({
      seizureFrequency: newValue.seizures === undefined && newValue.periods === undefined ? 'noCropping' : newValue,
    });
  };

  const handlePeriodsChange = (values: TOnChangeValues) => {
    const v = values['periods'] as number | undefined;
    const base: TEpilepsyESSeizureFrequencyType = typeof thisSearchField === 'object' ? thisSearchField : {};
    const newValue = { ...base, periods: typeof v === 'number' ? v : undefined };
    onChange({
      seizureFrequency: newValue.seizures === undefined && newValue.periods === undefined ? 'noCropping' : newValue,
    });
  };

  return (
    <React.Fragment>
      <StyledTitle style={{ marginTop: 0 }}>{fm('extendedSearch.seizureFrequencyTypes')}</StyledTitle>
      <SeizureClassificationComponent
        item={thisSearchField?.classification}
        i={0}
        criteria={criteria}
        onChangeCriteria={onChangeCriteria}
        multiple={true}
      />
      <StyledTitle>{fm('extendedSearch.seizureFrequency')}</StyledTitle>
      <InlineDiv>
        <Unit unit={fm('extendedSearch.seizureFrequencyPer28Days')}>
          <React.Fragment>
            <InputHandler
              type="NumberField"
              editing={true}
              name="minSeizures"
              formData={{
                onChange: handleSeizuresChange,
                document: { minSeizures: thisSearchField?.seizures?.minSeizures },
              }}
              min={0}
              placeholder="extendedSearch.min"
              width={7}
            />
            {' - '}
            <InputHandler
              type="NumberField"
              editing={true}
              name="maxSeizures"
              formData={{
                onChange: handleSeizuresChange,
                document: { maxSeizures: thisSearchField?.seizures?.maxSeizures },
              }}
              min={0}
              placeholder="extendedSearch.max"
              width={7}
            />
          </React.Fragment>
        </Unit>
      </InlineDiv>
      <InlineDiv>
        <Unit unit={fm('extendedSearch.seizureFrequencyPeriods')}>
          <InputHandler
            type="NumberField"
            editing={true}
            name="periods"
            formData={{
              onChange: handlePeriodsChange,
              document: { periods: thisSearchField?.periods },
            }}
            min={1}
            max={100}
            width={7}
          />
        </Unit>
      </InlineDiv>
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default SeizureFrequencyField;
